import React from "react";
import { Trash2 } from "lucide-react";
import { ProductColumnHeader } from "src/enums";
import { TableColumn, ProductAmendmentInputField, POProductConfirmation } from "src/types";
import { TextInputBox } from "src/components";
import { Table } from "src/components";
import { splitByWarehouseRatio } from "src/helpers";

interface OrderProductAmendmentInputTableProps {
  productAmendments: ProductAmendmentInputField[];
  setProductAmendments: React.Dispatch<React.SetStateAction<ProductAmendmentInputField[]>>;
  productConfirmations?: POProductConfirmation[];
}

const OrderProductAmendmentInputTable: React.FC<OrderProductAmendmentInputTableProps> = ({
  productAmendments,
  setProductAmendments,
  productConfirmations
}) => {
  const handleProductAmendmentChange = <T,>(id: string, fieldName: keyof ProductAmendmentInputField, value: T) => {
    setProductAmendments(
      productAmendments.map((amendedProduct) =>
        amendedProduct.fieldId === id ? { ...amendedProduct, [fieldName]: value } : amendedProduct
      )
    );
  };

  const showWarehouseBreakdown = productAmendments.some((p) => p.hasWarehouseSplit);

  const productAmendmentTableColumns: TableColumn<ProductColumnHeader, ProductAmendmentInputField>[] = [
    {
      header: ProductColumnHeader.ID,
      column: (row: ProductAmendmentInputField) => <div className="text-sm text-gray-700">{row.id}</div>,
      styles: { minWidth: "80px" }
    },
    {
      header: ProductColumnHeader.NAME,
      column: (row) => (
        <div className="min-w-[250px]">
          <TextInputBox
            id={`name-${row.fieldId}`}
            value={(
              productAmendments.find((amendedProduct) => amendedProduct.fieldId === row.fieldId)?.name ?? ""
            ).toString()}
            onChange={(value) => handleProductAmendmentChange(row.fieldId, "name", value)}
            maxLength={200}
            size="sm"
          />
        </div>
      ),
      styles: { minWidth: "250px" }
    },
    ...(showWarehouseBreakdown
      ? [
          {
            header: (
              <div className="flex flex-col items-center gap-1">
                <span className="text-xs">Order Qty</span>
                <span className="text-[11px] font-normal tracking-wider opacity-70">(auto)</span>
              </div>
            ),
            column: (row: ProductAmendmentInputField) => {
              const current = productAmendments.find((p) => p.fieldId === row.fieldId);
              const total = (Number(current?.amendedUkQty) || 0) + (Number(current?.amendedEuQty) || 0);
              return (
                <div className="flex items-center justify-center">
                  <span className="font-semibold text-secondary-900 tabular-nums text-sm">{total}</span>
                </div>
              );
            },
            styles: { minWidth: "90px", textAlign: "center" as const }
          },
          {
            header: <span className="text-violet-300 text-xs font-semibold tracking-wider">UK Qty</span>,
            column: (row: ProductAmendmentInputField) => (
              <div className="w-20">
                <TextInputBox
                  id={`uk-qty-${row.fieldId}`}
                  value={(productAmendments.find((p) => p.fieldId === row.fieldId)?.amendedUkQty ?? "").toString()}
                  onChange={(value) => handleProductAmendmentChange(row.fieldId, "amendedUkQty", value)}
                  maxLength={7}
                  size="sm"
                />
              </div>
            ),
            styles: { minWidth: "90px" }
          },
          {
            header: <span className="text-orange-300 text-xs font-semibold tracking-wider">EU Qty</span>,
            column: (row: ProductAmendmentInputField) => (
              <div className="w-20">
                <TextInputBox
                  id={`eu-qty-${row.fieldId}`}
                  value={(productAmendments.find((p) => p.fieldId === row.fieldId)?.amendedEuQty ?? "").toString()}
                  onChange={(value) => handleProductAmendmentChange(row.fieldId, "amendedEuQty", value)}
                  maxLength={7}
                  size="sm"
                />
              </div>
            ),
            styles: { minWidth: "90px" }
          },
          ...(productConfirmations
            ? [
                {
                  header: (
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-xs">Confirmed Qty</span>
                      <div className="flex items-center justify-center gap-3 text-[11px] font-semibold tracking-wider">
                        <span className="text-violet-300">UK</span>
                        <span className="text-orange-300">EU</span>
                      </div>
                    </div>
                  ),
                  column: (row: ProductAmendmentInputField) => {
                    const confirmedQty = productConfirmations.find((pc) => pc.productId === row.id)?.confirmedQty;
                    const current = productAmendments.find((p) => p.fieldId === row.fieldId);
                    const ukQty = Number(current?.amendedUkQty) || 0;
                    const euQty = Number(current?.amendedEuQty) || 0;
                    const { uk: confirmedUk, eu: confirmedEu } = splitByWarehouseRatio(confirmedQty, ukQty, euQty);

                    const confirmedQtyClass = confirmedQty === undefined ? "text-secondary-500" : "text-secondary-900";

                    return (
                      <div className="flex flex-col items-center gap-0.5">
                        <span className={`font-semibold tabular-nums text-sm ${confirmedQtyClass}`}>
                          {confirmedQty !== undefined ? confirmedQty : "-"}
                        </span>
                        {confirmedQty !== undefined && (
                          <div className="inline-grid grid-cols-[1fr_1fr] gap-x-3 text-sm font-medium tabular-nums">
                            <span className="text-right text-violet-600">{confirmedUk}</span>
                            <span className="text-left text-orange-600">{confirmedEu}</span>
                          </div>
                        )}
                      </div>
                    );
                  },
                  styles: { minWidth: "130px", textAlign: "center" as const }
                }
              ]
            : [])
        ]
      : [
          {
            header: ProductColumnHeader.ORDER_QUANTITY,
            column: (row: ProductAmendmentInputField) => (
              <div className="w-24">
                <TextInputBox
                  id={`qty-${row.fieldId}`}
                  value={(
                    productAmendments.find((amendedProduct) => amendedProduct.fieldId === row.fieldId)?.amendedQty ?? ""
                  ).toString()}
                  onChange={(value) => handleProductAmendmentChange(row.fieldId, "amendedQty", value)}
                  maxLength={10}
                  placeholder={`/ ${row.orderQty}`}
                  size="sm"
                />
              </div>
            ),
            styles: { minWidth: "120px" }
          },
          ...(productConfirmations
            ? [
                {
                  header: ProductColumnHeader.CONFIRMED_QTY,
                  column: (row: ProductAmendmentInputField) => {
                    const confirmedQty = productConfirmations.find((pc) => pc.productId === row.id)?.confirmedQty;
                    return (
                      <div className="text-sm text-gray-700 font-medium text-center">
                        {confirmedQty !== undefined ? confirmedQty : "-"}
                      </div>
                    );
                  },
                  styles: { minWidth: "120px", textAlign: "center" as const }
                }
              ]
            : [])
        ]),
    {
      header: ProductColumnHeader.SIZE,
      column: (row) => (
        <div className="w-28">
          <TextInputBox
            id={`size-${row.fieldId}`}
            value={(
              productAmendments.find((amendedProduct) => amendedProduct.fieldId === row.fieldId)?.size ?? ""
            ).toString()}
            onChange={(value) => handleProductAmendmentChange(row.fieldId, "size", value)}
            maxLength={20}
            size="sm"
          />
        </div>
      ),
      styles: { minWidth: "130px" }
    },
    {
      header: ProductColumnHeader.BARCODE,
      column: (row) => (
        <div className="min-w-[140px]">
          <TextInputBox
            id={`barcode-${row.fieldId}`}
            value={(
              productAmendments.find((amendedProduct) => amendedProduct.fieldId === row.fieldId)?.barcode ?? ""
            ).toString()}
            onChange={(value) => handleProductAmendmentChange(row.fieldId, "barcode", value)}
            maxLength={50}
            size="sm"
          />
        </div>
      ),
      styles: { minWidth: "140px" }
    },
    {
      header: ProductColumnHeader.REQUIRES_UK_RELABELLING,
      column: (row: ProductAmendmentInputField) => (
        <div className="flex items-center justify-center">
          <input
            type="checkbox"
            id={`uk-relabelling-${row.fieldId}`}
            checked={
              productAmendments.find((amendedProduct) => amendedProduct.fieldId === row.fieldId)
                ?.requiresUkRelabelling ?? false
            }
            onChange={(e) => handleProductAmendmentChange(row.fieldId, "requiresUkRelabelling", e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
        </div>
      ),
      styles: { minWidth: "100px", textAlign: "center" }
    },
    {
      header: ProductColumnHeader.REQUIRES_EU_RELABELLING,
      column: (row: ProductAmendmentInputField) => (
        <div className="flex items-center justify-center">
          <input
            type="checkbox"
            id={`eu-relabelling-${row.fieldId}`}
            checked={
              productAmendments.find((amendedProduct) => amendedProduct.fieldId === row.fieldId)
                ?.requiresEuRelabelling ?? false
            }
            onChange={(e) => handleProductAmendmentChange(row.fieldId, "requiresEuRelabelling", e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
        </div>
      ),
      styles: { minWidth: "100px", textAlign: "center" }
    },
    {
      header: ProductColumnHeader.IS_ANIMAL_SKU,
      column: (row: ProductAmendmentInputField) => {
        const current = productAmendments.find((amendedProduct) => amendedProduct.fieldId === row.fieldId);
        const isChecked = current?.isAnimalSku ?? false;
        return (
          <div className="flex items-center justify-center">
            <input
              type="checkbox"
              id={`animal-sku-${row.fieldId}`}
              checked={isChecked}
              disabled={current?.isAnimalNonCertified ?? false}
              onChange={(e) => handleProductAmendmentChange(row.fieldId, "isAnimalSku", e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
              title={current?.isAnimalNonCertified ? "Already marked Animal (Non-Certified)" : undefined}
            />
          </div>
        );
      },
      styles: { minWidth: "80px", textAlign: "center" }
    },
    {
      header: ProductColumnHeader.IS_ANIMAL_NON_CERTIFIED,
      column: (row: ProductAmendmentInputField) => {
        const current = productAmendments.find((amendedProduct) => amendedProduct.fieldId === row.fieldId);
        const isChecked = current?.isAnimalNonCertified ?? false;
        return (
          <div className="flex items-center justify-center">
            <input
              type="checkbox"
              id={`animal-non-certified-${row.fieldId}`}
              checked={isChecked}
              disabled={current?.isAnimalSku ?? false}
              onChange={(e) => handleProductAmendmentChange(row.fieldId, "isAnimalNonCertified", e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
              title={current?.isAnimalSku ? "Already marked Animal" : undefined}
            />
          </div>
        );
      },
      styles: { minWidth: "140px", textAlign: "center" }
    },
    {
      header: ProductColumnHeader.PLACE_HOLDER,
      column: (row) => (
        <button
          onClick={() =>
            setProductAmendments(productAmendments.filter((amendedProduct) => amendedProduct.fieldId !== row.fieldId))
          }
          disabled={productAmendments.length === 1}
          className="group flex items-center justify-center p-2 rounded-md hover:bg-error-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          title="Delete product"
          type="button"
        >
          <Trash2 className="w-4 h-4 text-error-600 group-hover:text-error-700 transition-colors" />
        </button>
      ),
      styles: { minWidth: "60px", textAlign: "center" }
    }
  ];

  return (
    <Table<ProductColumnHeader, ProductAmendmentInputField>
      data={productAmendments}
      columns={productAmendmentTableColumns}
    />
  );
};

export { OrderProductAmendmentInputTable };
